/**
 * Per-browser persistence for IFC file handles.
 *
 * Browsers can store FileSystemFileHandle objects in IndexedDB
 * (structured-clonable) so a session re-open can hand the user the
 * same file from disk without re-prompting, provided they keep
 * granting permission. We key by the SHA-256 of the IFC bytes that
 * the viewer already computes on every load, so the lookup is the
 * inverse of the session manifest entry.
 *
 * Behaviour is best-effort:
 * - On non-Chromium browsers the FS Access API is unavailable and
 *   every helper here returns null/false; callers fall back to the
 *   classic file picker.
 * - When a handle exists but the user revokes permission, we ask
 *   for it again. If they decline, we surface the failure and the
 *   user re-picks manually.
 * - Stored handles can become invalid when the user moves/renames
 *   the file on disk; we detect that on read and prune the row.
 */

import {
  modelAcceptAttr,
  modelPickerDescription,
  modelPickerExtensions,
} from '@core/session/modelFileTypes'
import { extractIfcFromZip, looksLikeZip } from '@viewer/lib/ifcZip'


const DB_NAME = 'vn2r.session'
const DB_VERSION = 1
const STORE = 'ifcHandles'

export function isFsAccessSupported(): boolean {
  return typeof window !== 'undefined' && 'showOpenFilePicker' in window
}

interface HandleRecord {
  sha256: string
  filename: string
  hint_path: string | null
  handle: FileSystemFileHandle
  saved_at: string
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'sha256' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDB open failed'))
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const store = tx.objectStore(STORE)
    const req = fn(store)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IDB op failed'))
    tx.oncomplete = () => db.close()
    tx.onerror = () => {
      db.close()
      reject(tx.error ?? new Error('IDB tx failed'))
    }
  })
}

export async function rememberHandle(opts: {
  sha256: string
  filename: string
  handle: FileSystemFileHandle
  hintPath?: string | null
}): Promise<void> {
  if (!isFsAccessSupported()) return
  try {
    const rec: HandleRecord = {
      sha256: opts.sha256.toLowerCase(),
      filename: opts.filename,
      hint_path: opts.hintPath ?? null,
      handle: opts.handle,
      saved_at: new Date().toISOString(),
    }
    await withStore('readwrite', (s) => s.put(rec))
  } catch {
    // IDB blocked / private mode: fall back silently to file pickers.
  }
}

async function loadHandleRecord(
  sha256: string,
): Promise<HandleRecord | null> {
  try {
    const rec = await withStore<unknown>('readonly', (s) =>
      s.get(sha256.toLowerCase()),
    )
    return (rec as HandleRecord | undefined) ?? null
  } catch {
    return null
  }
}

async function dropHandle(sha256: string): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(sha256.toLowerCase()))
  } catch {
    /* ignore */
  }
}

async function ensurePermission(
  handle: FileSystemFileHandle,
  prompt: boolean,
): Promise<boolean> {
  // queryPermission / requestPermission live behind any-cast because
  // TS lib doesn't ship the types yet on every platform.
  const h = handle as unknown as {
    queryPermission?: (opts: { mode: 'read' }) => Promise<PermissionState>
    requestPermission?: (opts: { mode: 'read' }) => Promise<PermissionState>
  }
  if (!h.queryPermission || !h.requestPermission) return true
  const cur = await h.queryPermission({ mode: 'read' })
  if (cur === 'granted') return true
  if (!prompt) return false
  const next = await h.requestPermission({ mode: 'read' })
  return next === 'granted'
}

async function readHandleAsFile(
  handle: FileSystemFileHandle,
): Promise<File | null> {
  try {
    return await handle.getFile()
  } catch {
    return null
  }
}

async function sha256OfBytes(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export type HandleResolution =
  | { kind: 'unsupported' }
  | { kind: 'missing' }
  | { kind: 'permission_denied'; filename: string }
  | { kind: 'gone'; filename: string }
  | { kind: 'sha_mismatch'; filename: string }
  | { kind: 'ok'; file: File }

/**
 * Try to re-read the file the user originally loaded for ``sha256``,
 * by going through the FileSystemFileHandle stored in IDB. Used by
 * Open Session to silently restore IFCs the user already approved
 * once. ``promptForPermission`` lets the caller decide whether to
 * trigger the browser prompt (true on user gesture, false when
 * probing on mount).
 */
export async function resolveStoredHandle(
  sha256: string,
  promptForPermission: boolean,
): Promise<HandleResolution> {
  if (!isFsAccessSupported()) return { kind: 'unsupported' }
  const rec = await loadHandleRecord(sha256)
  if (!rec) return { kind: 'missing' }
  const granted = await ensurePermission(rec.handle, promptForPermission)
  if (!granted) {
    return { kind: 'permission_denied', filename: rec.filename }
  }
  const file = await readHandleAsFile(rec.handle)
  if (!file) {
    await dropHandle(sha256)
    return { kind: 'gone', filename: rec.filename }
  }
  // Verify the file is still the same (user might have edited it).
  // If it changed we don't auto-load; that'd silently swap a
  // viewpoint's anchor file.
  const buf = await file.arrayBuffer()
  if (!(await mesmoConteudo(buf, sha256))) {
    return { kind: 'sha_mismatch', filename: rec.filename }
  }
  return { kind: 'ok', file }
}

/** O ficheiro do handle ainda é o que se abriu? Num `.ifczip` o SHA
 *  guardado é o do IFC de DENTRO (é sobre esse que o carregador o
 *  calcula), por isso compara-se com o IFC extraído pela mesma regra.
 *  Comparar com o zip inteiro dava sempre "modificado no disco". */
async function mesmoConteudo(buf: ArrayBuffer, sha256: string): Promise<boolean> {
  const esperado = sha256.toLowerCase()
  if ((await sha256OfBytes(buf)).toLowerCase() === esperado) return true
  const bytes = new Uint8Array(buf)
  if (!looksLikeZip(bytes)) return false
  try {
    const interior = extractIfcFromZip(bytes).bytes
    const copia = interior.slice().buffer as ArrayBuffer
    return (await sha256OfBytes(copia)).toLowerCase() === esperado
  } catch {
    return false
  }
}

export async function pickIfcFile(): Promise<{
  file: File
  handle: FileSystemFileHandle | null
} | null> {
  // Prefer FS Access API (gives us a handle to remember) but fall back
  // to a classic <input type=file> for browsers that don't ship it.
  if (isFsAccessSupported()) {
    try {
      const w = window as unknown as {
        showOpenFilePicker: (opts: {
          types: { description: string; accept: Record<string, string[]> }[]
          multiple: boolean
          excludeAcceptAllOption: boolean
        }) => Promise<FileSystemFileHandle[]>
      }
      const [handle] = await w.showOpenFilePicker({
        types: [
          {
            description: modelPickerDescription(),
            accept: {
              'application/octet-stream': modelPickerExtensions(),
            },
          },
          {
            description: 'IFC zipado',
            accept: { 'application/zip': ['.ifczip'] },
          },
        ],
        multiple: false,
        excludeAcceptAllOption: false,
      })
      if (!handle) return null
      const file = await handle.getFile()
      return { file, handle }
    } catch (err) {
      // User cancelled
      if (err instanceof DOMException && err.name === 'AbortError') return null
      // Any other failure: fall back to legacy picker below
    }
  }
  return await legacyPickFile()
}

export async function pickIfcFiles(): Promise<{
  file: File
  handle: FileSystemFileHandle | null
}[]> {
  if (isFsAccessSupported()) {
    try {
      const w = window as unknown as {
        showOpenFilePicker: (opts: {
          types: { description: string; accept: Record<string, string[]> }[]
          multiple: boolean
          excludeAcceptAllOption: boolean
        }) => Promise<FileSystemFileHandle[]>
      }
      const handles = await w.showOpenFilePicker({
        types: [
          {
            description: modelPickerDescription(),
            accept: {
              'application/octet-stream': modelPickerExtensions(),
            },
          },
          {
            description: 'IFC zipado',
            accept: { 'application/zip': ['.ifczip'] },
          },
        ],
        multiple: true,
        excludeAcceptAllOption: false,
      })
      const out: { file: File; handle: FileSystemFileHandle | null }[] = []
      for (const h of handles) {
        const f = await h.getFile()
        out.push({ file: f, handle: h })
      }
      return out
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return []
    }
  }
  const single = await legacyPickFile(true)
  return single ? [{ file: single.file, handle: null }] : []
}

function legacyPickFile(
  multiple: boolean = false,
): Promise<{ file: File; handle: null } | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = modelAcceptAttr()
    if (multiple) input.multiple = true
    input.style.display = 'none'
    input.addEventListener('change', () => {
      const file = input.files?.[0]
      input.remove()
      resolve(file ? { file, handle: null } : null)
    })
    document.body.appendChild(input)
    input.click()
  })
}
